import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./GiftComponent.css";
import { getCurrencySymbol } from "../../helper/utils";

const GiftComponent = ({ event, paymentDetails }) => {
  const navigate = useNavigate();

  const [selectedOption, setSelectedOption] = useState("asoEbi");
  const [showAmount, setShowAmount] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [checkInFilter, setCheckInFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const ITEMS_PER_PAGE = 5;

  const toggleAmountVisibility = () => {
    setShowAmount(!showAmount);
  };

  const formatAmount = (amount, currency) => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${Number(amount).toLocaleString()}`;
  };

  const calculateEachPaymentTotal = (paymentDetails = []) => {
    let totalCashGiftPaid = 0;
    let totalEventAttirePaid = 0;
    let totalTicketFeePaid = 0;

    paymentDetails.forEach((payment) => {
      totalCashGiftPaid += payment.totalCashGiftPaid || 0;
      totalEventAttirePaid += payment.totalEventAttirePaid || 0;
      totalTicketFeePaid += payment.totalTicketFeePaid || 0;
    });

    const totalPaid =
      totalCashGiftPaid + totalEventAttirePaid + totalTicketFeePaid;

    return {
      totalCashGiftPaid,
      totalEventAttirePaid,
      totalTicketFeePaid,
      totalPaid,
    };
  };

  const totals = calculateEachPaymentTotal(paymentDetails);

  const filteredGuestData = () => {
    if (paymentDetails && paymentDetails.length > 0) {
      return paymentDetails.map((payment) => ({
        ...payment.guestId,
        invitationStatus: payment.status,
        responseDate: payment.responseDate,
        numberOfGuests: payment.numberOfGuests,
        _id: payment._id,
        payment,
        hasPaidTicket: payment.ticketFeePaid?.some((t) => t.paid) || false,
        hasPaidAttire: payment.eventAttire?.some((a) => a.paid) || false,
        hasPaidCashGift: payment.cashGiftPaid?.giftPaid || false,
        isCheckedIn: payment.isCheckedIn || false,
        checkInTime: payment.checkInTime || null,
      }));
    }
    return [];
  };

  const getFilteredGuests = () => {
    let guests = filteredGuestData();

    if (selectedOption === "asoEbi") {
      guests = guests.filter((g) => g.hasPaidAttire);
    } else if (selectedOption === "cashGift") {
      guests = guests.filter((g) => g.hasPaidCashGift);
    } else if (selectedOption === "ticketFee") {
      guests = guests.filter((g) => g.hasPaidTicket);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      guests = guests.filter((guest) => {
        const fullName = `${guest.firstName} ${guest.lastName}`.toLowerCase();
        const email = guest.email?.toLowerCase() || "";
        const phone = guest.phoneNumber || "";
        return (
          fullName.includes(query) ||
          email.includes(query) ||
          phone.includes(query)
        );
      });
    }

    if (checkInFilter !== "all") {
      guests = guests.filter((guest) => {
        if (checkInFilter === "checked-in") {
          return guest.isCheckedIn === true;
        } else if (checkInFilter === "not-checked-in") {
          return guest.invitationStatus === "accepted" && !guest.isCheckedIn;
        }
        return true;
      });
    }

    return guests;
  };

  const getPaginatedGuests = () => {
    const filteredGuests = getFilteredGuests();
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredGuests.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(getFilteredGuests().length / ITEMS_PER_PAGE);

  const guestData = filteredGuestData();
  const ticketPaidCount = guestData.filter((g) => g.hasPaidTicket).length;
  const attirePaidCount = guestData.filter((g) => g.hasPaidAttire).length;
  const cashGiftPaidCount = guestData.filter((g) => g.hasPaidCashGift).length;

  const handleViewAll = () => {
    setIsModalOpen(true);
    setSearchQuery("");
    setCheckInFilter("all");
    setCurrentPage(1);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSearchQuery("");
    setCheckInFilter("all");
    setCurrentPage(1);
  };

  const renderGuestCard = (guest) => (
    <div key={guest._id} className="gift-guest-card">
      <div className="gift-guest-info">
        <div className="gift-guest-avatar">
          <img
            src={guest.userPhoto || "/images/avatar.webp"}
            alt={`${guest.firstName} ${guest.lastName}`}
            onError={(e) => (e.target.src = "/images/avatar.webp")}
          />
          {guest.isCheckedIn && (
            <div className="gift-checkin-badge">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"
                  fill="white"
                />
              </svg>
            </div>
          )}
        </div>
        <div className="gift-guest-details">
          <h4>{`${guest.firstName} ${guest.lastName}`}</h4>
          <p className="gift-guest-phone">{guest.phoneNumber}</p>
        </div>
      </div>
      <div className="gift-guest-badges">
        {guest.isCheckedIn && (
          <span className="gift-badge gift-checked-in">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"
                fill="currentColor"
              />
            </svg>
            Checked In
          </span>
        )}
        {guest.hasPaidAttire && (
          <span className="gift-badge gift-attire">Attire</span>
        )}
        {guest.hasPaidTicket && (
          <span className="gift-badge gift-ticket">Ticket</span>
        )}
        {guest.hasPaidCashGift && (
          <span className="gift-badge gift-cash">Cash Gift</span>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    let title = "";
    let totalAmount = 0;
    let paidCount = 0;

    switch (selectedOption) {
      case "asoEbi":
        title = "Event Attire Funds";
        totalAmount = totals.totalEventAttirePaid;
        paidCount = attirePaidCount;
        break;
      case "cashGift":
        title = "Cash Gift";
        totalAmount = totals.totalCashGiftPaid;
        paidCount = cashGiftPaidCount;
        break;
      case "ticketFee":
        title = "Ticket Gift";
        totalAmount = totals.totalTicketFeePaid;
        paidCount = ticketPaidCount;
        break;
    }

    return (
      <div className="gift-header-section">
        <div className="gift-funds-container">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z"
              fill="#006400"
            />
          </svg>
          <h3>{title}</h3>
        </div>

        <div className="gift-currency-container">
          <div className="gift-flag">
            {event?.preferredCurrency === "USD" ? "US" : "NG"}
          </div>
          <div>
            {event?.preferredCurrency === "USD"
              ? "US Dollar"
              : "Nigerian Naira"}
          </div>
        </div>

        <div className="gift-amount-container">
          <span className="gift-amount">
            {showAmount
              ? formatAmount(totalAmount, event?.preferredCurrency || "NGN")
              : "••••••••"}
          </span>
          <button
            onClick={toggleAmountVisibility}
            className="gift-eye-button"
            aria-label={showAmount ? "Hide amount" : "Show amount"}
          >
            {showAmount ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
        </div>

        {paidCount > 0 ? (
          <div className="gift-total-guests">
            <span>Total Guests: {paidCount}</span>
            <button onClick={handleViewAll} className="gift-view-all-btn">
              View All
            </button>
          </div>
        ) : (
          <p className="gift-no-payment">No Payment Yet</p>
        )}
      </div>
    );
  };

  const renderGuestListModal = () => {
    if (!isModalOpen) return null;

    const filteredGuests = getFilteredGuests();
    const paginatedGuests = getPaginatedGuests();
    const checkedInCount = filteredGuests.filter((g) => g.isCheckedIn).length;
    const notCheckedInCount = filteredGuests.length - checkedInCount;

    let modalTitle = "";
    switch (selectedOption) {
      case "asoEbi":
        modalTitle = "Event Attire Guest List";
        break;
      case "cashGift":
        modalTitle = "Cash Gift Guest List";
        break;
      case "ticketFee":
        modalTitle = "Ticket Fee Guest List";
        break;
    }

    return (
      <div className="gift-modal-overlay" onClick={handleCloseModal}>
        <div
          className="gift-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="gift-modal-header">
            <h2>{modalTitle}</h2>
            <button
              onClick={handleCloseModal}
              className="gift-modal-close-btn"
              aria-label="Close modal"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>

          <div className="gift-modal-body">
            {/* Search Bar */}
            <div className="gift-search-container">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                  fill="#666"
                />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, or phone"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="gift-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="gift-clear-btn"
                  aria-label="Clear search"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                      fill="#666"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="gift-filter-container">
              <button
                className={`gift-filter-btn ${
                  checkInFilter === "all" ? "gift-filter-active" : ""
                }`}
                onClick={() => {
                  setCheckInFilter("all");
                  setCurrentPage(1);
                }}
              >
                All
              </button>
              <button
                className={`gift-filter-btn ${
                  checkInFilter === "checked-in" ? "gift-filter-active" : ""
                }`}
                onClick={() => {
                  setCheckInFilter("checked-in");
                  setCurrentPage(1);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"
                    fill="currentColor"
                  />
                </svg>
                Checked In ({checkedInCount})
              </button>
              <button
                className={`gift-filter-btn ${
                  checkInFilter === "not-checked-in" ? "gift-filter-active" : ""
                }`}
                onClick={() => {
                  setCheckInFilter("not-checked-in");
                  setCurrentPage(1);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
                    fill="currentColor"
                  />
                </svg>
                Not Checked In ({notCheckedInCount})
              </button>
            </div>

            {/* Guest List */}
            <div className="gift-guest-list">
              {paginatedGuests.length === 0 ? (
                <div className="gift-empty-state">
                  <svg
                    width="60"
                    height="60"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
                      fill="#ccc"
                    />
                  </svg>
                  <p>
                    {searchQuery
                      ? "No guests found matching your search"
                      : checkInFilter === "checked-in"
                      ? "No guests have checked in yet"
                      : checkInFilter === "not-checked-in"
                      ? "All guests have been checked in"
                      : "No guests found"}
                  </p>
                </div>
              ) : (
                paginatedGuests.map(renderGuestCard)
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="gift-pagination">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="gift-page-btn"
                >
                  Previous
                </button>
                <span className="gift-page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="gift-page-btn"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="gift-component-container">
        <div className="gift-tab-container">
          <button
            className={`gift-tab ${
              selectedOption === "asoEbi" ? "gift-tab-active" : ""
            }`}
            onClick={() => {
              setSelectedOption("asoEbi");
              setSearchQuery("");
              setCheckInFilter("all");
              setCurrentPage(1);
            }}
          >
            Aso Ebi
          </button>
          <button
            className={`gift-tab ${
              selectedOption === "cashGift" ? "gift-tab-active" : ""
            }`}
            onClick={() => {
              setSelectedOption("cashGift");
              setSearchQuery("");
              setCheckInFilter("all");
              setCurrentPage(1);
            }}
          >
            Cash Gift
          </button>
          {event?.ticketFee && (
            <button
              className={`gift-tab ${
                selectedOption === "ticketFee" ? "gift-tab-active" : ""
              }`}
              onClick={() => {
                setSelectedOption("ticketFee");
                setSearchQuery("");
                setCheckInFilter("all");
                setCurrentPage(1);
              }}
            >
              Ticket Fee
            </button>
          )}
        </div>

        <div className="gift-content-container">{renderContent()}</div>
      </div>

      {renderGuestListModal()}
    </>
  );
};

export default GiftComponent;
